import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import _map from 'lodash/map';
import { Button } from '@material-ui/core';

import PropertyField from '../form/property-field';
import { getInitLineValues, getLineFields } from './helpers';
import { checkSubmitIsDisabled } from '../form/helpers';

const useStyles = makeStyles((theme) => ({
  form: {},
  fields: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(2, 0),
    flexDirection: 'column',
  },
  fieldWrapper: {
    width: '100%',
    flex: 1,
    maxWidth: '100%',
    display: 'flex',
    backgroundColor: theme.palette.background.card,
    padding: theme.spacing(2),
    margin: theme.spacing(1, 0),
    position: 'relative',
    flexDirection: 'column',
  },
  field: {
    width: '100%',
    '& + &': {
      marginTop: theme.spacing(2),
    },
  },
  listField: {
    width: '100%',
  },
  stringField: {
    width: '100%',
  },
}));

const ExploreForm = (props) => {
  const { onSubmit, initialValues, exploreLines } = props;
  const classes = useStyles();
  const VALUE_PREFIX = 'propertyValue';
  // Carry BOTH ids on each field: the real property.id (drives the option
  // search — which list items populate the box) and lineId = explore_lines row
  // id (drives the form-value namespace so two rows sharing one property stay
  // independent). The line name overrides the label. See getLineFields — do NOT
  // collapse these two ids, or a club box ends up searching whatever property
  // shares the line id (football_sponsor=6, icehockey_sponsor=7).
  const lineProperties = _map(exploreLines, ({ id, name, property }) => ({
    ...property,
    lineId: id,
    name,
  }));
  const initPropertyValues = getInitLineValues(exploreLines, VALUE_PREFIX);
  const formikInitValues = {
    ...initialValues,
    ...initPropertyValues,
  };

  const formik = useFormik({
    initialValues: formikInitValues,
    onSubmit,
    validate: () => ({}),
  });
  const { values: formValues, handleSubmit } = formik;
  const fields = getLineFields(lineProperties, formValues, VALUE_PREFIX);
  const submitIsDisabled = checkSubmitIsDisabled(formik);

  const renderListField = (property) => (
    <div key={property?.formName || property?.id} className={classes.fieldWrapper}>
      <div className={classes.field}>
        <PropertyField
          property={property}
          formik={formik}
          classes={{ list: classes.listField }}
          formName={property?.formName}
        />
      </div>
    </div>
  );

  const renderColorField = (property) => {
    const formName = `${property?.lineId}.nameValue`;
    const colorNameFiledProps = {
      formik,
      classes: { string: classes.stringField },
      property: {
        formName,
        name: 'Colors line name',
        id: formName,
        property_type: 'String',
      },
    };
    return (
      <div key={property?.formName || property?.id} className={classes.fieldWrapper}>
        <div className={classes.field}>
          <PropertyField {...colorNameFiledProps} />
        </div>
        <div className={classes.field}>
          <PropertyField property={property} formik={formik} formName={property?.formName} />
        </div>
      </div>
    );
  };

  const renderField = (property) => {
    const type = property?.property_type;

    if (type === 'Color') return renderColorField(property);

    return renderListField(property);
  };

  const renderFields = <div className={classes.fields}>{_map(fields, renderField)}</div>;

  return (
    <form onSubmit={handleSubmit} className={classes.form}>
      {renderFields}
      <Button type="submit" variant="contained" color="secondary" disabled={submitIsDisabled}>
        Save
      </Button>
    </form>
  );
};

ExploreForm.propTypes = {
  exploreLines: PropTypes.array,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
};

ExploreForm.defaultProps = {
  exploreLines: [],
  initialValues: {},
};

export default ExploreForm;
